<template>
  <div
    class="modal fade"
    id="orderDetailsModal"
    tabindex="-1"
    aria-labelledby="orderDetailsLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content border-0 shadow-lg rounded-3">
        <!-- Header -->
        <div class="modal-header bg-dark text-white">
          <h5 class="modal-title text-white" id="orderDetailsLabel">
            Order Details - #{{ order?.order_id }}
          </h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <!-- Order Summary -->
          <section class="mb-4">
            <h6 class="fw-bold border-bottom pb-2 mb-3">Order Summary</h6>
            <div class="row g-3">
              <div class="col-md-6">
                <p class="mb-1"><strong>Order ID:</strong> {{ order?.order_id }}</p>
                <p class="mb-1"><strong>Date:</strong> {{ formatDate(order?.created_at) }}</p>
                <p class="mb-1"><strong>Status:</strong>
                  <span
                    class="badge text-uppercase"
                    :class="commonStore.getStatusClass(order?.order_process_status)"
                  >
                    {{ order?.order_process_status }}
                  </span>
                </p>
              </div>
              <div class="col-md-6">
                <p class="mb-1"><strong>Payment Type:</strong> {{ order?.payment_type }}</p>
                <p class="mb-1"><strong>Payment Status:</strong>
                  <span
                    class="badge"
                    :class="order?.payment_status === 'COMPLETED' ? 'bg-success' : 'bg-warning text-dark'"
                  >
                    {{ order?.payment_status }}
                  </span>
                </p>
                <p class="mb-1"><strong>Transaction ID:</strong> {{ order?.transaction_id }}</p>
              </div>
            </div>
          </section>

          <!-- Customer Info -->
          <section class="mb-4">
            <h6 class="fw-bold border-bottom pb-2 mb-3">Customer Info</h6>
            <div class="row">
              <div class="col-md-6">
                <p><strong>Name:</strong> {{ order?.billing_address?.first_name }} {{ order?.billing_address?.last_name }}</p>
                <p><strong>Email:</strong> {{ order?.billing_address?.email }}</p>
                <p><strong>Mobile:</strong> {{ order?.billing_address?.mobile_no }}</p>
              </div>
              <div class="col-md-6">
                <p><strong>Delivery Option:</strong> {{ order?.delivery_option }}</p>
                <p><strong>Pickup/Delivery Time:</strong> {{ order?.pickup_datetime }}</p>
              </div>
            </div>
          </section>

          <!-- Product Info -->
          <section class="mb-4">
            <h6 class="fw-bold border-bottom pb-2 mb-3">Ordered Items</h6>
            <div
              v-for="(item, index) in order?.cart_data"
              :key="index"
              class="d-flex align-items-center border rounded p-3 mb-3 shadow-sm bg-light position-relative"
            >
              <a :href="`${item.slug}`" target="_blank">
                <img
                  :src="item.image"
                  class="rounded me-3"
                  alt="Product"
                  style="width: 80px; height: 80px; object-fit: cover;"
                />
              </a>

              <div class="flex-grow-1">
                <h6 class="mb-1">
                  <a :href="`${item.slug}`" target="_blank">{{ item.name }}</a>
                </h6>
                <p class="mb-1 text-muted">
                  {{ item.attributes?.map(a => `${a.attribute}: ${a.attribute_value}`).join(', ') }}
                </p>

                <!-- Message -->
                <div v-if="order.order_details[index].isEditing">                  
                  <input
                    type="text"
                    v-model="order.order_details[index].message_on_cake"
                    class="form-control form-control-sm mb-2"
                    placeholder="Message on Cake"
                  />                  
                  <textarea
                    v-model="order.order_details[index].special_instruction"
                    class="form-control form-control-sm"
                    placeholder="Special Instruction"
                    rows="2"
                  ></textarea>
                </div>
                <div v-else>
                  <small>Message: <em>{{ order.order_details[index]?.message_on_cake || '-' }}</em></small><br />
                  <small>Instruction: <em>{{ order.order_details[index]?.special_instruction || '-' }}</em></small>
                </div>
              </div>

              <div class="text-end">
                <p class="fw-bold mb-0">${{ item.price }}</p>
                <p class="text-muted mb-0">x{{ item.quantity }}</p>
              </div>

              <!-- Edit / Save Button -->
              <button
                v-if="order.order_process_status === 'process' || order.order_process_status === 'layered'"
                class="btn btn-sm btn-outline-primary position-absolute"
                style="top: 10px; right: 10px;"
                @click="toggleEdit(order.order_details[index])"
              >
                <i v-if="order.order_details[index].isEditing" class="fas fa-save"></i>                
                <i v-else class="fas fa-edit"></i>
              </button>
              <button
                v-if="order.order_details[index].isEditing"
                class="btn btn-sm btn-outline-danger position-absolute"
                style="top: 10px; right: 50px;"
                @click="order.order_details[index].isEditing = false"
              >
                <i class="fas fa-times"></i>                
              </button>
            </div>
          </section>

          <!-- Payment Summary -->
          <section>
            <h6 class="fw-bold border-bottom pb-2 mb-3">Payment Summary</h6>
            <ul class="list-group list-group-flush">
              <li class="list-group-item d-flex justify-content-between">
                <span>Subtotal</span> <strong>${{ order?.subtotal }}</strong>
              </li>
              <li class="list-group-item d-flex justify-content-between">
                <span>Tax</span> <strong>${{ order?.tax }}</strong>
              </li>
              <li class="list-group-item d-flex justify-content-between">
                <span>Chocolate Plaque</span> <strong>${{ order?.chocolate_plaque_charge }}</strong>
              </li>
              <li class="list-group-item d-flex justify-content-between">
                <span>Discount ({{ order?.discount_percentage }}%)</span>
                <strong>-${{ order?.discount_amount }}</strong>
              </li>
              <li class="list-group-item d-flex justify-content-between fw-bold">
                <span>Grand Total</span>
                <strong>${{ order?.grand_total }}</strong>
              </li>
            </ul>
          </section>
        </div>

        <!-- Footer -->
        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <!-- <button type="button" class="btn btn-dark">Download Invoice</button> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { api } from '@/plugins/axios'
import { useCommonStore } from '@/stores/commonStore'
import { onMounted } from 'vue'
import { toast } from 'vue3-toastify'

const commonStore = useCommonStore()

const props = defineProps({
  order: {
    type: Object,
    required: true
  }
})

onMounted(() => {
  const modalEl = document.getElementById('orderDetailsModal')
  if (modalEl && window.bootstrap) {
    new window.bootstrap.Modal(modalEl, {
      backdrop: 'static',
      keyboard: true
    })
  }

  if(props.order.order_details){
    props.order.order_details.forEach(item => {
      item.isEditing = false
      item.message_on_cake = item.message_on_cake || ''
      item.special_instruction = item.special_instruction || ''
    })
  }  
})

function toggleEdit(item) {
  if (item.isEditing) {
    // Save changes
    item.message_on_cake = item.message_on_cake
    item.special_instruction = item.special_instruction

    let payload = {
      message_on_cake: item.message_on_cake,
      special_instruction: item.special_instruction
    }    
    
    api.post(`edit-order-details/${item.order_id}/${item.id}`, payload).then((response) => {      
      toast.success(response.data.message)
    }).catch((err) => {
      console.error('Cart save failed:', err)
      toast.error(err.response.data.results[0]||err.response.data.message)
    })
  }
  item.isEditing = !item.isEditing
}

const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  return date.toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short'
  })
}

</script>

<style scoped>
.modal-content {
  font-family: 'Inter', sans-serif;
}
.list-group-item {
  background: transparent;
  border: 0;
  border-bottom: 1px solid #f1f1f1;
}
.list-group-item:last-child {
  border-bottom: none;
}
.modal {
  z-index: 9999 !important;
}
</style>
